// Time segments (parity backoffice pages/settings-system-hardware-reporting.md §4.1, SCR-045/REP-015; api-commerce
// catalog.md R-C25; field note backoffice.reports.sales.time-segment): the location's dayparts (Breakfast, Lunch, …) as
// a name and a start and end time, managed on Settings › Reporting › Time Segments and read by the Time Segments report.
// Location config written at the node or in the cloud, one `ConfigFieldsWritten` fact per write, like void reasons.
// The register never reads them (legacy did not broadcast them).
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { LocationId, TimeSegmentId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';

/** A wall-clock time to the minute, `HH:MM` (legacy `H:i`, stored `HH:MM:SS`). */
export const WallTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM (24-hour)');

/**
 * Legacy `time_segments` (name, `start_at`, `end_at`; all three required). An end before the start (22:00–02:00) is kept
 * as given; the report files orders under it as legacy does (B200, core `segmentMinutes`) until parity is validated.
 */
export const TimeSegmentFields = z
  .object({ name: z.string().trim().min(1).max(255), startAt: WallTime, endAt: WallTime })
  .meta({ id: 'TimeSegmentFields' });
export const TimeSegmentView = TimeSegmentFields.extend({ timeSegmentId: TimeSegmentId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'TimeSegmentView' });

export const TimeSegmentNotFound = defineError({
  code: 'reporting.not_found', httpStatus: 404, message: 'errors.reporting.not_found', retryable: false,
  details: z.object({ entity: z.string(), id: z.string() }),
});

const common = {
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten],
  errors: [CloudAuthoritative, ProposalRequired, TimeSegmentNotFound], dryRun: true,
} as const;

export const UpsertTimeSegment = defineCommand({
  ...common,
  name: 'reporting.upsertTimeSegment',
  summary: 'Add a time segment (a named daypart with a start and end time) or change it.',
  input: z.object({ timeSegmentId: TimeSegmentId, locationId: LocationId, changes: TimeSegmentFields.partial() }),
  output: TimeSegmentView,
  permission: 'settings.reporting.manage',
  risk: 'low',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/time-segments', 'PUT /v1/time-segments/{id}'],
});

export const DeleteTimeSegment = defineCommand({
  ...common,
  name: 'reporting.deleteTimeSegment',
  summary: 'Delete a time segment; the Time Segments report no longer lists it.',
  input: z.object({ timeSegmentId: TimeSegmentId, locationId: LocationId }),
  output: z.object({ id: TimeSegmentId, deleted: z.literal(true) }),
  permission: 'settings.reporting.manage',
  risk: 'low',
  agent: { expose: false },
  legacy: ['DELETE /v1/time-segments/{id}'],
});

export const ListTimeSegments = defineQuery({
  availability: 'location', scope: 'location', errors: [], agent: { expose: true, surfaces: ['backoffice.assistant'] }, permission: { session: 'user' },
  name: 'reporting.timeSegments.list',
  summary: "The location's time segments, by start time (legacy lists them by `start_at`).",
  input: z.object({ locationId: LocationId }),
  output: z.object({ timeSegments: z.array(TimeSegmentView) }),
  legacy: ['GET /v1/time-segments'],
});

export const timeSegmentCommands = [UpsertTimeSegment, DeleteTimeSegment] as const;
export const timeSegmentQueries = [ListTimeSegments] as const;
